import { useEffect, useState } from "react";
import { Logo } from "./Logo";
import { useLang } from "../hooks/useLang";
import { getMe } from "../services/api";
import Icon, { type IconName } from "./icons";

export type Mode = "home" | "analyze" | "gspr" | "documents" | "docanalysis" | "translate" | "chat" | "help" | "support" | "settings";

const ITEMS: { mode: Mode; icon: IconName; labelKey: string }[] = [
  { mode: "home", icon: "home", labelKey: "mode_home" },
  { mode: "analyze", icon: "search", labelKey: "mode_analyze" },
  { mode: "gspr", icon: "clipboard", labelKey: "mode_gspr" },
  { mode: "documents", icon: "file", labelKey: "mode_documents" },
  { mode: "docanalysis", icon: "chartBar", labelKey: "mode_docanalysis" },
  { mode: "translate", icon: "languages", labelKey: "mode_translate" },
  { mode: "chat", icon: "chat", labelKey: "mode_chat" },
  { mode: "help", icon: "book", labelKey: "mode_help" },
  { mode: "support", icon: "headset", labelKey: "mode_support" },
  { mode: "settings", icon: "settings", labelKey: "mode_settings" },
];

// Etiketlerdeki baştaki emoji'yi (📖/⚙️) ayıkla — ikon ayrı kolonda gösteriliyor.
const stripEmoji = (s: string) => s.replace(/^[^\p{L}\p{N}]+/u, "").trim();

type Props = {
  mode: Mode;
  onSelect: (m: Mode) => void;
  version: string;
  theme: "light" | "dark";
  onToggleTheme: () => void;
  userEmail?: string;
  onLogout: () => void;
  /** Mobil drawer açık mı (≤900px). Masaüstünde yok sayılır. */
  open?: boolean;
  /** Drawer'ı kapat (öğe seçimi / kapat butonu). */
  onClose?: () => void;
};

export default function LeftNav({ mode, onSelect, version, theme, onToggleTheme, userEmail, onLogout, open, onClose }: Props) {
  const { t } = useLang();
  const themeLabel = theme === "dark" ? t("theme_light") : t("theme_dark");
  const select = (m: Mode) => { onSelect(m); onClose?.(); };
  // Kredi bakiyesi — her sayfada görünür. Navigasyonda (mode değişince) tazelenir,
  // böylece kredi düşen işlemlerden sonra güncel görünür.
  const [credit, setCredit] = useState<number | null>(null);
  useEffect(() => {
    getMe().then((m) => setCredit(m.credit_balance)).catch(() => {});
  }, [mode]);
  return (
    <nav className={"leftnav" + (open ? " open" : "")}>
      <div className="leftnav-brand">
        {/* Önce burada favicon <img> olarak basılıyordu: kare ikon, beyaz
            plakalı — beyaz kenar çubuğunda plaka kayboluyor, yanına da düz
            yazı marka adı geliyordu. Gerçek logo tek parça ve temaya duyarlı. */}
        <Logo className="brand-logo" />
        <button className="leftnav-close" onClick={onClose} aria-label={t("nav_close")} title={t("nav_close")}>
          <Icon name="x" size={18} />
        </button>
      </div>

      <button
        className="leftnav-credit"
        onClick={() => { localStorage.setItem("settings_tab", "credits"); select("settings"); }}
        title={t("nav_credit_title")}
      >
        <span className="leftnav-credit-ico" aria-hidden>🪙</span>
        <span className="leftnav-credit-body">
          <span className="leftnav-credit-val">{credit == null ? "…" : credit.toLocaleString("tr-TR")}</span>
          <span className="leftnav-credit-lbl">{t("credits_short")}</span>
        </span>
      </button>

      <div className="leftnav-items">
        {ITEMS.map((it) => (
          <button
            key={it.mode}
            className={"leftnav-item" + (mode === it.mode ? " active" : "")}
            onClick={() => select(it.mode)}
            title={stripEmoji(t(it.labelKey))}
          >
            <span className="leftnav-ico" aria-hidden><Icon name={it.icon} /></span>
            <span className="leftnav-label">{stripEmoji(t(it.labelKey))}</span>
          </button>
        ))}
      </div>

      <div className="leftnav-foot">
        <button className="leftnav-tool" onClick={onToggleTheme} title={themeLabel}>
          <span className="leftnav-ico" aria-hidden><Icon name={theme === "dark" ? "sun" : "moon"} /></span>
          <span className="leftnav-label">{themeLabel}</span>
        </button>
        {userEmail && <div className="leftnav-who" title={userEmail}>{userEmail}</div>}
        <button className="leftnav-tool" onClick={onLogout} title="Çıkış">
          <span className="leftnav-ico" aria-hidden><Icon name="logOut" /></span>
          <span className="leftnav-label">Çıkış</span>
        </button>
        <div className="leftnav-version">v{version}</div>
      </div>
    </nav>
  );
}
